import { useState } from "react";
import { useMutation } from "@tanstack/react-query";

import { fleet, type BulkPreview, type BulkResult } from "@/api/client";
import { ActionRow } from "@/components/ActionRow";
import { FilePick } from "@/components/FilePick";
import { Icon } from "@/components/icons";
import { Select } from "@/components/inputs";
import { actionClass, type CommandSpec } from "@/modules/fleet/catalogue";

/** Bulk: review, THEN act (OFL-07).
 *
 *  ONE TARGET LIST, NOT TWO. A CSV pick PARSES INTO THE TEXTAREA rather than
 *  arming a parallel hidden list. That is deliberate: a panel with a paste box
 *  and a separate file path has two sources of truth for "who gets stopped",
 *  and the operator cannot see which one the button will use. The console has
 *  already shipped that bug once — one element driving two forms (D-84's
 *  render-test catalogue) — and the blast radius here is vehicles. Filling the
 *  textarea keeps the list visible and editable before anything is sent.
 *
 *  THE NUMBER THAT MATTERS IS `targeted`, NOT `requested`. FOTA taught this
 *  the expensive way: when matched and targeted were allowed to differ
 *  silently, an operator was told a rollout covered a different number of
 *  devices than it would. So the three counts are shown apart and every
 *  skipped asset is named with its reason.
 */
export function BulkPanel({ spec, mayCommand }: {
  spec: CommandSpec;
  mayCommand: boolean;
}) {
  const [text, setText] = useState("");
  const [fileName, setFileName] = useState("");
  const [action, setAction] = useState<number>(spec.options[0]?.action ?? 0);
  const [note, setNote] = useState("");
  //: same sentinel as the single-asset path: "" = the server's default
  const [lifeMin, setLifeMin] = useState("");
  const [preview, setPreview] = useState<BulkPreview | null>(null);
  const [done, setDone] = useState<BulkResult | null>(null);
  const [parseError, setParseError] = useState("");

  const assetIds = text.split(/[\s,]+/).map((x) => x.trim())
    .filter((x) => x && !x.startsWith("#"));

  const doPreview = useMutation({
    mutationFn: () => fleet.bulkPreview(assetIds),
    onSuccess: (r) => { setPreview(r); setDone(null); },
  });
  const doRun = useMutation({
    // DISPATCHED ON THE COMMAND, not hardcoded. This called
    // `bulkImmobilize` whatever was selected, so choosing MOBILISE posted a
    // release to the immobilise endpoint with `action` 0 — which the
    // `action IN (2, 3)` check constraint rejects, so a bulk release 500'd.
    // The catalogue said mobilise was bulk-capable and no endpoint existed.
    mutationFn: () => {
      const life = lifeMin ? Number(lifeMin) : undefined;
      return spec.key === "mobilize"
        ? fleet.bulkMobilize(assetIds, note || undefined, life)
        : fleet.bulkImmobilize(assetIds, action, note || undefined, life);
    },
    onSuccess: (r) => { setDone(r); setPreview(null); },
  });

  /** The template's own header and comment lines are dropped, so an operator
   *  who downloads it, adds three rows and uploads it back does not get
   *  "asset_id" treated as an asset. */
  const onFile = async (f: File | null) => {
    setFileName(f?.name ?? "");
    setParseError("");
    setPreview(null);
    if (!f) return;
    try {
      // STRIP THE BOM FIRST. Excel writes a UTF-8 BOM when it saves a CSV, so
      // the first cell arrives as "﻿asset_id" — which does not equal
      // "asset_id", so the HEADER ROW was about to be uploaded as an asset id.
      // That happens to anyone who opens our own template in Excel and saves
      // it, which is the common path rather than an edge case.
      const text = (await f.text()).replace(/^﻿/, "");
      const rows = text.split(/\r?\n/)
        // Excel quotes any cell holding a comma or a leading zero; take the
        // first column and unwrap it, rather than keeping the quotes as part
        // of the id and then reporting it as "not found".
        .map((l) => l.split(",")[0].trim().replace(/^"(.*)"$/, "$1").trim())
        .filter((l) => l && !l.startsWith("#")
                       && l.toLowerCase() !== "asset_id");
      if (rows.length === 0) {
        setParseError("that file has no asset rows in its first column");
        return;
      }
      setText(rows.join("\n"));
    } catch (e) {
      setParseError(e instanceof Error ? e.message : String(e));
    }
  };

  const err = doPreview.isError ? doPreview.error
    : doRun.isError ? doRun.error : null;

  return (
    <div className="panel panel-pad">
      {/* REFERENCE STRIP, the shape `CsvUpload` uses for Enroll and Allocate:
          a line of explanation on the left, the template as a BUTTON on the
          right, then a divider before the real controls.

          It was a bare inline <a> inside 12.5px muted text, and Dattatray
          could not find it — *"In bulk appy I still dont see a template
          CSV!"*. A control that reads as prose is not a control. The shared
          component had already settled this; this panel is hand-built because
          its target list is a textarea a CSV FILLS rather than a hidden
          parsed list, so the pattern is matched by hand here. */}
      <div style={{ display: "flex", gap: 14, alignItems: "flex-start",
                    justifyContent: "space-between", flexWrap: "wrap" }}>
        <div style={{ minWidth: 220, flex: "1 1 320px" }}>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".8px",
                        textTransform: "uppercase", color: "var(--t-mute)" }}>
            Reference sheet
          </div>
          <div style={{ fontSize: 11.5, color: "var(--t-dim)", marginTop: 4 }}>
            One column, <span className="mono">asset_id</span>, one asset per
            row. The {spec.label.toLowerCase()} type and your level come from
            this page, not the file — a per-row type would let a spreadsheet
            decide that row 47 gets a wheel lock.
          </div>
        </div>
        {/* ONE TEMPLATE PER COMMAND, and it names the command in its header
            and filename, so a sheet prepared to release a depot cannot be
            uploaded into an immobilise run unseen. */}
        <a className="btn ghost" href={fleet.bulkTemplateUrl(spec.key)}>
          <Icon name="download" /> Download {spec.label.toLowerCase()} template (.csv)
        </a>
      </div>

      <div style={{ height: 1, background: "var(--line)", margin: "16px 0" }} />

      <div style={{ fontSize: 12.5, color: "var(--t-mute)", marginBottom: 10 }}>
        Paste asset ids — one per line or comma separated — or choose a CSV,
        which fills this same box so you can see and edit the list before
        anything is sent.
      </div>

      <textarea className="inp" rows={4} value={text}
        placeholder={"DRILL-ASSET-001\nDRILL-ASSET-002"}
        onChange={(e) => { setText(e.target.value); setPreview(null); setDone(null); }} />

      <div style={{ display: "flex", gap: 10, alignItems: "flex-end",
                    flexWrap: "wrap", marginTop: 10 }}>
        {spec.options.length > 0 && (
          <div>
            <label className="simlab">{spec.label} as</label>
            <Select style={{ width: 210 }} value={action}
              onChange={(e) => setAction(Number(e.target.value))}>
              {spec.options.map((o) => (
                <option key={o.action} value={o.action}>{o.label}</option>
              ))}
            </Select>
          </div>
        )}
        <div>
          <label className="simlab">Retry delivery for</label>
          <Select style={{ width: 170 }} value={lifeMin}
            onChange={(e) => setLifeMin(e.target.value)}>
            <option value="">
              Default ({spec.key === "mobilize" ? "15 min" : "24 h"})
            </option>
            <option value="5">5 minutes</option>
            <option value="15">15 minutes</option>
            <option value="60">1 hour</option>
            <option value="360">6 hours</option>
            <option value="1440">24 hours</option>
            <option value="4320">3 days</option>
            <option value="10080">7 days</option>
          </Select>
        </div>
        <div>
          <label className="simlab">Note (optional)</label>
          <input className="inp" style={{ width: 220 }} placeholder="non-payment, batch 14"
            value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
      </div>

      <ActionRow>
        <span style={{ fontSize: 11.5, color: "var(--t-mute)", marginRight: "auto" }}>
          {assetIds.length} asset{assetIds.length === 1 ? "" : "s"} listed
        </span>
        <FilePick accept=".csv,text/csv" label="Choose .csv" fileName={fileName}
          onFile={onFile} before />
        <button className="btn ghost"
          disabled={assetIds.length === 0 || doPreview.isPending}
          onClick={() => doPreview.mutate()}>Preview</button>
        {/* ARMED ONLY BY A PREVIEW. Not a convenience — the preview is what
            tells the operator how many will ACTUALLY be commanded, and a run
            button live before it would make that review optional. */}
        {/* CLASS AND ICON FROM THE SPEC, never literals. This button read
            `btn red` + "blocked" with `{spec.label}` interpolated, so
            choosing MOBILISE rendered a red "Mobilise 3" wearing a ban
            symbol — the release control dressed as the destructive one. */}
        <button className={actionClass(spec, true)}
          disabled={!preview || preview.targeted === 0 || preview.over_limit
                    || !mayCommand || doRun.isPending}
          onClick={() => doRun.mutate()}>
          <Icon name={spec.icon} /> {spec.label} {preview?.targeted ?? 0}
        </button>
      </ActionRow>

      {!mayCommand && (
        <div style={{ fontSize: 11.5, color: "var(--t-mute)" }}>
          You can preview a run. Sending it needs the immobilise permission.
        </div>
      )}
      {parseError && (
        <div style={{ fontSize: 11.5, color: "var(--bad)" }}>{parseError}</div>
      )}
      {err && (
        <div style={{ fontSize: 11.5, color: "var(--bad)", marginTop: 6 }}>
          {String((err as Error)?.message ?? "bulk run failed")}
        </div>
      )}

      {preview && (
        <div style={{ marginTop: 12, fontSize: 12.5 }}>
          <b>{preview.requested}</b> requested ·{" "}
          <b style={{ color: "var(--bad)" }}>{preview.targeted}</b> will be
          commanded ·{" "}
          <b style={{ color: "var(--warn)" }}>{preview.skipped}</b> skipped
          {preview.over_limit && (
            <div style={{ color: "var(--bad)", marginTop: 6 }}>
              Over the {preview.limit}-asset ceiling. Split the run — the limit
              is about blast radius, not performance.
            </div>
          )}
          {preview.skipped_detail.length > 0 && (
            <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
              {preview.skipped_detail.map((d) => (
                <li key={d.asset_id} className="mono" style={{ fontSize: 11.5 }}>
                  {d.asset_id} — {d.reason === "NOT_ACTUATABLE"
                    ? "no telematics box installed"
                    : "not found, or not yours"}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {done && (
        <div style={{ marginTop: 12, fontSize: 12.5 }}>
          Batch <span className="mono">{done.batch_id.slice(0, 8)}</span>:
          commanded <b>{done.commanded}</b> of {done.requested}.
          {done.failed_to_publish.length > 0 && (
            <div style={{ color: "var(--warn)", marginTop: 6 }}>
              {done.failed_to_publish.length} could not be published and are
              still pending. The holds ARE recorded — a bulk run is not atomic,
              and rolling back holds because one publish failed would
              un-immobilise vehicles somebody decided to stop.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
